import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Target, Eye, TrendingUp, Calendar, Plus, Save, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSVTO = () => {
  const [vto, setVto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [coreValueInput, setCoreValueInput] = useState('');

  // Form state
  const [vtoForm, setVtoForm] = useState({
    core_values: [],
    core_focus: { purpose: '', niche: '' },
    ten_year_target: '',
    marketing_strategy: { target_market: '', unique_value: '', three_uniques: '' },
    three_year_picture: { revenue: '', profit: '', measurables: '' },
    one_year_plan: { revenue: '', profit: '', goals: [] }
  });

  useEffect(() => {
    fetchVTO();
  }, []);

  const fetchVTO = async () => {
    try {
      const response = await axios.get(`${API}/eos/vto`);
      setVto(response.data);
      setVtoForm({
        core_values: response.data.core_values || [],
        core_focus: response.data.core_focus || { purpose: '', niche: '' },
        ten_year_target: response.data.ten_year_target || '',
        marketing_strategy: response.data.marketing_strategy || { target_market: '', unique_value: '', three_uniques: '' },
        three_year_picture: response.data.three_year_picture || { revenue: '', profit: '', measurables: '' },
        one_year_plan: response.data.one_year_plan || { revenue: '', profit: '', goals: [] }
      });
    } catch (error) {
      console.error('Error fetching V/TO:', error);
      toast.error('Failed to load V/TO');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/eos/vto`, vtoForm);
      toast.success('V/TO saved successfully!');
      setEditing(false);
      fetchVTO();
    } catch (error) {
      console.error('Error saving V/TO:', error);
      toast.error('Failed to save V/TO');
    }
  };

  const handleAddCoreValue = () => {
    if (!coreValueInput.trim()) return;
    
    const newValue = { value: coreValueInput, description: '', behaviors: [] };
    setVtoForm({ ...vtoForm, core_values: [...vtoForm.core_values, newValue] });
    setCoreValueInput('');
  };

  const handleRemoveCoreValue = (index) => {
    const newValues = vtoForm.core_values.filter((_, idx) => idx !== index);
    setVtoForm({ ...vtoForm, core_values: newValues });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading V/TO...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold">Vision/Traction Organizer</h1>
              <p className="text-sm text-muted-foreground">
                Define your company's vision and track your progress
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={handleSave}>
                  <Save className="h-4 w-4 mr-2" />
                  Save V/TO
                </Button>
              </>
            ) : (
              <Button onClick={() => setEditing(true)}>
                <Edit2 className="h-4 w-4 mr-2" />
                Edit V/TO
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue="vision">
          <TabsList>
            <TabsTrigger value="vision">Vision</TabsTrigger>
            <TabsTrigger value="traction">Traction</TabsTrigger>
          </TabsList>

          {/* Vision Tab */}
          <TabsContent value="vision" className="space-y-6 mt-6">
            {/* Core Values */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-purple-600" />
                  Core Values
                </CardTitle>
                <CardDescription>
                  3-7 core values that define who you are as a company
                </CardDescription>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter a core value"
                        value={coreValueInput}
                        onChange={(e) => setCoreValueInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddCoreValue()}
                      />
                      <Button onClick={handleAddCoreValue}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {vtoForm.core_values.map((cv, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                          <span className="flex-1 font-medium">{cv.value}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveCoreValue(idx)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {vtoForm.core_values.length > 0 ? (
                      vtoForm.core_values.map((cv, idx) => (
                        <div key={idx} className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950">
                          <div className="font-semibold text-purple-900 dark:text-purple-100">
                            {cv.value}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground col-span-2">No core values defined yet</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Core Focus */}
            <Card>
              <CardHeader>
                <CardTitle>Core Focus</CardTitle>
                <CardDescription>Your company's purpose and niche</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div>
                      <Label>Purpose (Why do we exist?)</Label>
                      <Textarea
                        placeholder="Our purpose is to..."
                        rows={3}
                        value={vtoForm.core_focus.purpose}
                        onChange={(e) => setVtoForm({
                          ...vtoForm,
                          core_focus: { ...vtoForm.core_focus, purpose: e.target.value }
                        })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Niche (What do we do?)</Label>
                      <Textarea
                        placeholder="We provide..."
                        rows={3}
                        value={vtoForm.core_focus.niche}
                        onChange={(e) => setVtoForm({
                          ...vtoForm,
                          core_focus: { ...vtoForm.core_focus, niche: e.target.value }
                        })}
                        className="mt-2"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="font-semibold mb-1">Purpose:</div>
                      <p className="text-muted-foreground">
                        {vtoForm.core_focus.purpose || 'Not defined yet'}
                      </p>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Niche:</div>
                      <p className="text-muted-foreground">
                        {vtoForm.core_focus.niche || 'Not defined yet'}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* 10-Year Target */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  10-Year Target
                </CardTitle>
                <CardDescription>
                  Your long-term, audacious goal
                </CardDescription>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <Textarea
                    placeholder="In 10 years, we will..."
                    rows={4}
                    value={vtoForm.ten_year_target}
                    onChange={(e) => setVtoForm({ ...vtoForm, ten_year_target: e.target.value })}
                  />
                ) : (
                  <p className="text-lg">
                    {vtoForm.ten_year_target || 'Not defined yet'}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Marketing Strategy */}
            <Card>
              <CardHeader>
                <CardTitle>Marketing Strategy</CardTitle>
                <CardDescription>Target market and unique value proposition</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div>
                      <Label>Target Market</Label>
                      <Input
                        placeholder="Who are your ideal customers?"
                        value={vtoForm.marketing_strategy.target_market}
                        onChange={(e) => setVtoForm({
                          ...vtoForm,
                          marketing_strategy: { ...vtoForm.marketing_strategy, target_market: e.target.value }
                        })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Unique Value Proposition</Label>
                      <Textarea
                        placeholder="What makes you different?"
                        rows={3}
                        value={vtoForm.marketing_strategy.unique_value}
                        onChange={(e) => setVtoForm({
                          ...vtoForm,
                          marketing_strategy: { ...vtoForm.marketing_strategy, unique_value: e.target.value }
                        })}
                        className="mt-2"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="font-semibold mb-1">Target Market:</div>
                      <p className="text-muted-foreground">
                        {vtoForm.marketing_strategy.target_market || 'Not defined yet'}
                      </p>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Unique Value:</div>
                      <p className="text-muted-foreground">
                        {vtoForm.marketing_strategy.unique_value || 'Not defined yet'}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Traction Tab */}
          <TabsContent value="traction" className="space-y-6 mt-6">
            {/* 3-Year Picture */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  3-Year Picture
                </CardTitle>
                <CardDescription>
                  Where will you be in 3 years?
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div>
                      <Label>Revenue Target</Label>
                      <Input
                        placeholder="e.g., $10M"
                        value={vtoForm.three_year_picture.revenue}
                        onChange={(e) => setVtoForm({
                          ...vtoForm,
                          three_year_picture: { ...vtoForm.three_year_picture, revenue: e.target.value }
                        })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Profit Target</Label>
                      <Input
                        placeholder="e.g., $2M"
                        value={vtoForm.three_year_picture.profit}
                        onChange={(e) => setVtoForm({
                          ...vtoForm,
                          three_year_picture: { ...vtoForm.three_year_picture, profit: e.target.value }
                        })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Key Measurables</Label>
                      <Textarea
                        placeholder="What will you measure?"
                        rows={4}
                        value={vtoForm.three_year_picture.measurables}
                        onChange={(e) => setVtoForm({
                          ...vtoForm,
                          three_year_picture: { ...vtoForm.three_year_picture, measurables: e.target.value }
                        })}
                        className="mt-2"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="font-semibold mb-1">Revenue:</div>
                        <p className="text-2xl font-bold text-green-600">
                          {vtoForm.three_year_picture.revenue || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <div className="font-semibold mb-1">Profit:</div>
                        <p className="text-2xl font-bold text-blue-600">
                          {vtoForm.three_year_picture.profit || 'Not set'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Measurables:</div>
                      <p className="text-muted-foreground whitespace-pre-line">
                        {vtoForm.three_year_picture.measurables || 'Not defined yet'}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* 1-Year Plan */}
            <Card>
              <CardHeader>
                <CardTitle>1-Year Plan</CardTitle>
                <CardDescription>
                  Your goals for this year
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div>
                      <Label>Revenue Goal</Label>
                      <Input
                        placeholder="e.g., $3M"
                        value={vtoForm.one_year_plan.revenue}
                        onChange={(e) => setVtoForm({
                          ...vtoForm,
                          one_year_plan: { ...vtoForm.one_year_plan, revenue: e.target.value }
                        })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Profit Goal</Label>
                      <Input
                        placeholder="e.g., $600K"
                        value={vtoForm.one_year_plan.profit}
                        onChange={(e) => setVtoForm({
                          ...vtoForm,
                          one_year_plan: { ...vtoForm.one_year_plan, profit: e.target.value }
                        })}
                        className="mt-2"
                      />
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="font-semibold mb-1">Revenue:</div>
                      <p className="text-2xl font-bold text-green-600">
                        {vtoForm.one_year_plan.revenue || 'Not set'}
                      </p>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Profit:</div>
                      <p className="text-2xl font-bold text-blue-600">
                        {vtoForm.one_year_plan.profit || 'Not set'}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default EOSVTO;